import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CaseClosureTracker, AttendanceArchive } from "@/pages/dashboard/Compliance";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, FileText, Loader2, Upload, Check, X, AlertTriangle, Sparkles, FolderArchive, Users, CalendarRange, Printer, Trash2, Archive, KeyRound, ShieldCheck } from "lucide-react";

// Inline popover the director taps next to every "No roster" row in the
// importer preview. Shows the top-3 closest roster spellings + each
// similarity %, so they can tell at a glance whether a missing match is
// a typo in the CSV vs a child that needs to be enrolled.
function NoRosterCell({ row, onLink }) {
  const [open, setOpen] = useState(false);
  const near = row?.near_matches || [];
  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid={`records-no-roster-${row.child_first}-${row.child_last}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] hover:bg-amber-100"
        title="Tap to see closest roster spellings"
      >
        <AlertTriangle className="w-2.5 h-2.5" /> No roster
        {near.length > 0 && <ChevronDown className="w-2.5 h-2.5 opacity-70" />}
      </button>
      {open && near.length > 0 && (
        <div
          data-testid="records-near-matches-popover"
          className="absolute right-0 top-full mt-1 z-20 w-72 rounded-lg border border-stone-200 bg-white shadow-lg p-3 text-left space-y-2"
        >
          <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">
            Closest roster spellings
          </div>
          <div className="space-y-1.5">
            {near.map((n, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-stone-800 truncate">{n.display}</div>
                </div>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono shrink-0 ${n.similarity >= 70 ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-500"}`}>
                  {n.similarity}%
                </span>
                <button
                  type="button"
                  data-testid={`records-use-this-${n.roster_id}`}
                  onClick={() => { onLink && onLink(n.roster_id, n.display); setOpen(false); }}
                  className="shrink-0 px-2 py-1 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-semibold"
                >Use this</button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-stone-500 leading-snug border-t border-stone-100 pt-2">
            <b>Use this</b> re-links the row to the child you tap — no need to edit the source CSV.
            If none of these are right, add a new enrollment from the <b>Children</b> page.
          </p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10px] text-stone-500 hover:text-stone-800"
          >Close</button>
        </div>
      )}
      {open && near.length === 0 && (
        <div className="absolute right-0 top-full mt-1 z-20 w-64 rounded-lg border border-stone-200 bg-white shadow-lg p-3 text-left">
          <p className="text-xs text-stone-700">
            No similar names on the roster. This child is probably not yet enrolled — add them from the <b>Children</b> page.
          </p>
          <button type="button" onClick={() => setOpen(false)} className="mt-2 text-[10px] text-stone-500 hover:text-stone-800">Close</button>
        </div>
      )}
    </div>
  );
}


// Reusable collapsible card. Records page sections (Cases, Daily PDF
// Importer, Attendance Archive) all share this so they look like one
// stack instead of a wall of content.
function Collapsible({ id, title, subtitle, Icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={`records-section-${id}`} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <button
        type="button"
        data-testid={`records-section-toggle-${id}`}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 px-6 py-4 text-left transition-colors ${open ? "bg-stone-900 text-white" : "bg-white hover:bg-stone-50 text-stone-900"}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5" />}
          <div>
            <div className="font-display text-lg font-medium">{title}</div>
            {subtitle && (
              <div className={`text-xs mt-0.5 ${open ? "text-stone-300" : "text-stone-500"}`}>{subtitle}</div>
            )}
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div data-testid={`records-section-body-${id}`} className="border-t border-stone-200 p-6">{children}</div>}
    </section>
  );
}

// Print archive — every successful Daily PDF import is logged here so
// directors can pull it up months later for a DCY audit. Grouped by
// Year → Month → Week (the Monday of each week the events touched).
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Per user directive 2026-02-19 — every date the director sees must be
// MM/DD/YYYY (US). Accepts "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS…" and
// returns "" when the input isn't a recognisable ISO date.
const usDate = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return iso;
  return `${m[2]}/${m[3]}/${m[1]}`;
};

function DailyPrintArchive({ refreshKey = 0 }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance/daily-pdf/imports");
      setImports(r.data?.imports || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  const printUrl = (id) => {
    const token = (typeof window !== "undefined" && localStorage.getItem("cc_token")) || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/attendance/daily-pdf/imports/${id}/print.pdf?t=${encodeURIComponent(token)}`;
  };
  const view = (imp) => window.open(printUrl(imp.id), "_blank", "noopener,noreferrer");
  const printIt = (imp) => {
    const w = window.open(printUrl(imp.id), "_blank", "noopener,noreferrer");
    if (w) { try { w.addEventListener("load", () => { try { w.print(); } catch (_e1) { /* PDF viewer blocks programmatic print */ } }); } catch (_e2) { /* cross-origin guard */ } }
  };
  const download = (imp) => {
    const a = document.createElement("a");
    a.href = printUrl(imp.id);
    a.download = `DCY01208-${usDate(imp.date_from).replaceAll("/", "-") || imp.id}.pdf`;
    a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const remove = async (imp) => {
    if (!window.confirm(`Remove this import from the audit archive?\n\nThis only removes the print record — the attendance events themselves stay in the system.`)) return;
    setBusyId(imp.id);
    try {
      await api.delete(`/attendance/daily-pdf/imports/${imp.id}`);
      toast.success("Removed from audit archive.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  // Group: Year → Month → Week (Monday). The "weeks" array on each
  // import tells us which Mondays the events touched.
  const grouped = useMemo(() => {
    const byYear = new Map();
    for (const imp of imports) {
      // An import can span multiple weeks. We index it under EVERY week
      // it touched so an auditor looking at "Week of May 25" sees it.
      const weeks = (imp.weeks && imp.weeks.length) ? imp.weeks : [imp.date_from].filter(Boolean);
      for (const wkMon of weeks) {
        const [y, m] = (wkMon || "").split("-");
        if (!y || !m) continue;
        if (!byYear.has(y)) byYear.set(y, new Map());
        const yr = byYear.get(y);
        if (!yr.has(m)) yr.set(m, new Map());
        const mo = yr.get(m);
        if (!mo.has(wkMon)) mo.set(wkMon, []);
        mo.get(wkMon).push(imp);
      }
    }
    return Array.from(byYear.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([y, mm]) => ({
        year: y,
        months: Array.from(mm.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([m, ww]) => ({
            month: m,
            label: MONTH_NAMES[parseInt(m, 10) - 1] || m,
            weeks: Array.from(ww.entries())
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([wk, list]) => ({ week: wk, imports: list })),
          })),
      }));
  }, [imports]);

  // Default-open: the most recent year + month + week.
  const initialOpen = useMemo(() => {
    const o = { y: {}, m: {}, w: {} };
    if (grouped.length) {
      o.y[grouped[0].year] = true;
      if (grouped[0].months.length) {
        const mKey = `${grouped[0].year}-${grouped[0].months[0].month}`;
        o.m[mKey] = true;
        if (grouped[0].months[0].weeks.length) o.w[grouped[0].months[0].weeks[0].week] = true;
      }
    }
    return o;
  }, [grouped]);
  const [openY, setOpenY] = useState(initialOpen.y);
  const [openM, setOpenM] = useState(initialOpen.m);
  const [openW, setOpenW] = useState(initialOpen.w);
  useEffect(() => { setOpenY(initialOpen.y); setOpenM(initialOpen.m); setOpenW(initialOpen.w); }, [initialOpen]);

  if (loading) {
    return <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;
  }
  if (imports.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 p-8 text-center">
        <Archive className="w-8 h-8 mx-auto text-stone-400" />
        <p className="mt-3 text-sm text-stone-600">No Daily PDF imports yet.</p>
        <p className="text-xs text-stone-500 mt-1">After your first successful import, the print-ready record will live here forever — sorted by year / month / week.</p>
      </div>
    );
  }

  return (
    <div data-testid="records-daily-print-archive" className="space-y-3">
      {grouped.map(({ year, months }) => {
        const totalImports = months.reduce((s, m) => s + m.weeks.reduce((ss, w) => ss + w.imports.length, 0), 0);
        const yOpen = !!openY[year];
        return (
          <div key={year} className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`print-archive-year-${year}`}>
            <button
              type="button"
              data-testid={`print-archive-year-toggle-${year}`}
              onClick={() => setOpenY((o) => ({ ...o, [year]: !o[year] }))}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-stone-900 text-white hover:bg-stone-800 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                {yOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-display text-lg font-medium">{year}</span>
              </div>
              <span className="text-[11px] uppercase tracking-widest text-stone-300">
                {months.length} month{months.length === 1 ? "" : "s"} · {totalImports} print{totalImports === 1 ? "" : "s"}
              </span>
            </button>
            {yOpen && (
              <div className="divide-y divide-stone-100">
                {months.map((m) => {
                  const mKey = `${year}-${m.month}`;
                  const mOpen = !!openM[mKey];
                  const mImports = m.weeks.reduce((s, w) => s + w.imports.length, 0);
                  const monthPrintUrl = (() => {
                    const token = (typeof window !== "undefined" && localStorage.getItem("cc_token")) || "";
                    return `${process.env.REACT_APP_BACKEND_URL}/api/attendance/daily-pdf/imports/month/${mKey}/print.pdf?t=${encodeURIComponent(token)}`;
                  })();
                  const printMonth = (e) => {
                    e.stopPropagation();
                    const w = window.open(monthPrintUrl, "_blank", "noopener,noreferrer");
                    if (w) { try { w.addEventListener("load", () => { try { w.print(); } catch (_e1) { /* viewer blocks */ } }); } catch (_e2) { /* cross-origin */ } }
                  };
                  return (
                    <div key={mKey}>
                      <div className="flex items-center justify-between gap-3 px-5 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors">
                        <button
                          type="button"
                          data-testid={`print-archive-month-toggle-${mKey}`}
                          onClick={() => setOpenM((o) => ({ ...o, [mKey]: !o[mKey] }))}
                          className="flex-1 flex items-center gap-3 text-left"
                        >
                          {mOpen ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
                          <span className="font-medium text-stone-900">{m.label}</span>
                        </button>
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] uppercase tracking-widest text-stone-500">{m.weeks.length} week{m.weeks.length === 1 ? "" : "s"} · {mImports} print{mImports === 1 ? "" : "s"}</span>
                          <Button
                            data-testid={`print-archive-month-print-${mKey}`}
                            size="sm"
                            onClick={printMonth}
                            className="rounded-lg h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                            title="Print every week's roster for this month as one binder PDF"
                          >
                            <Printer className="w-3 h-3 mr-1" /> Print this month
                          </Button>
                        </div>
                      </div>
                      {mOpen && (
                        <div className="divide-y divide-stone-100">
                          {m.weeks.map((w) => {
                            const wOpen = !!openW[w.week];
                            return (
                              <div key={w.week} className="bg-white">
                                <button
                                  type="button"
                                  data-testid={`print-archive-week-toggle-${w.week}`}
                                  onClick={() => setOpenW((o) => ({ ...o, [w.week]: !o[w.week] }))}
                                  className="w-full flex items-center justify-between gap-3 pl-9 pr-5 py-2 hover:bg-emerald-50/20 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-3">
                                    {wOpen ? <ChevronDown className="w-3.5 h-3.5 text-stone-500" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-500" />}
                                    <span className="text-sm text-stone-800">Week of {w.week}</span>
                                  </div>
                                  <span className="text-[10px] uppercase tracking-widest text-stone-500">{w.imports.length} import{w.imports.length === 1 ? "" : "s"}</span>
                                </button>
                                {wOpen && (
                                  <table className="w-full text-sm">
                                    <thead className="bg-white text-[10px] uppercase tracking-widest text-stone-400">
                                      <tr>
                                        <th className="text-left pl-16 pr-3 py-1.5 font-normal">Range</th>
                                        <th className="text-right px-3 py-1.5 font-normal">Inserted</th>
                                        <th className="text-right px-3 py-1.5 font-normal">Skipped</th>
                                        <th className="text-left px-3 py-1.5 font-normal">Imported</th>
                                        <th className="text-right px-3 py-1.5 font-normal"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {w.imports.map((imp) => (
                                        <tr key={imp.id} data-testid={`print-archive-row-${imp.id}`} className="border-t border-stone-100 hover:bg-stone-50/40">
                                          <td className="pl-16 pr-3 py-2">
                                            <div className="font-medium text-stone-900">{usDate(imp.date_from)} → {usDate(imp.date_to)}</div>
                                            <div className="text-[10px] text-stone-500">{imp.row_count} row{imp.row_count === 1 ? "" : "s"} extracted</div>
                                          </td>
                                          <td className="px-3 py-2 text-right font-display text-emerald-700">{imp.inserted}</td>
                                          <td className="px-3 py-2 text-right font-display text-amber-700">{imp.skipped}</td>
                                          <td className="px-3 py-2 text-stone-500 text-xs">
                                            <div>{new Date(imp.imported_at).toLocaleDateString()}</div>
                                            <div className="text-[10px] text-stone-400 truncate max-w-[180px]">{imp.imported_by}</div>
                                          </td>
                                          <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <Button data-testid={`print-archive-view-${imp.id}`} variant="outline" size="sm" onClick={() => view(imp)} className="rounded-lg h-7 text-xs mr-1 border-stone-300">
                                              <FileText className="w-3 h-3 mr-1" /> View
                                            </Button>
                                            <Button data-testid={`print-archive-print-${imp.id}`} size="sm" onClick={() => printIt(imp)} className="rounded-lg h-7 text-xs mr-1 bg-emerald-700 hover:bg-emerald-800 text-white">
                                              <Printer className="w-3 h-3 mr-1" /> Print
                                            </Button>
                                            <Button data-testid={`print-archive-download-${imp.id}`} variant="outline" size="sm" onClick={() => download(imp)} className="rounded-lg h-7 text-xs mr-1 border-stone-300">
                                              <Archive className="w-3 h-3" />
                                            </Button>
                                            <Button data-testid={`print-archive-delete-${imp.id}`} variant="outline" size="sm" disabled={busyId === imp.id} onClick={() => remove(imp)} className="rounded-lg h-7 text-xs border-red-300 text-red-700 hover:bg-red-50">
                                              {busyId === imp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Daily Detailed Attendance PDF importer — reads a typed report
// (KinderConnect, ProCare, Brightwheel) and auto-fills each child's
// IN/OUT events into the digital attendance archive.
//
// Flow: Upload → backend kicks off Gemini job → frontend polls every 3s
// → preview table shows every extracted row (green = match found in
// roster, amber = name didn't match) → director clicks "Import all
// matched" to commit. De-duped by (child, date, kind) so re-running
// the same PDF is safe. On success a record lands in the Daily print
// archive (organised by year/month/week) so auditors can re-print months
// later.
function DailyPdfImporter({ onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // "running" | "done" | "error"
  const [jobId, setJobId] = useState(null);
  const [events, setEvents] = useState([]);
  const [filename, setFilename] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // Per user 2026-02-19: the "We saved your last extraction" resume
  // banner kept reappearing after every successful upload because
  // un-consumed jobs linger for 24h in the backend. The director
  // explicitly does NOT want this banner anymore — CSV/Excel uploads
  // are instant so there is nothing meaningful to resume. We keep the
  // backend `/jobs/latest` endpoint around in case it's useful later
  // but stop calling it on mount so the banner never shows.

  const reset = () => {
    setJobStatus(null); setJobId(null); setEvents([]); setFilename(""); setResult(null); setError(""); setProgressLabel("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const [progressLabel, setProgressLabel] = useState("");

  const pollUntilDone = async (id) => {
    // Backend chunks the PDF into 4-page batches and runs them
    // concurrently. Even very long reports finish in ~3 minutes. We
    // poll up to 15 minutes (300 × 3s) to give big production PDFs
    // plenty of headroom.
    let tries = 0;
    let consecutiveErrors = 0;
    while (tries < 300) {
      await new Promise((r) => setTimeout(r, 3000));
      tries += 1;
      try {
        const poll = await api.get(`/attendance/daily-pdf/jobs/${id}`);
        const j = poll.data || {};
        consecutiveErrors = 0;
        if (j.progress_label) setProgressLabel(j.progress_label);
        if (j.status === "done") {
          setEvents(Array.isArray(j.events) ? j.events : []);
          setJobStatus("done");
          const chunkErr = (j.chunk_errors && j.chunk_errors.length) ? ` · ${j.chunk_errors.length} chunk${j.chunk_errors.length === 1 ? "" : "s"} skipped` : "";
          toast.success(`AI extracted ${j.row_count} attendance rows${chunkErr}.`);
          return;
        }
        if (j.status === "error") {
          setJobStatus("error");
          setError(j.error || "Extraction failed.");
          toast.error(j.error || "Extraction failed.");
          return;
        }
      } catch (err) {
        consecutiveErrors += 1;
        // Give up if we can't reach the backend for ~30s straight —
        // either the session expired or the server is down.
        if (consecutiveErrors >= 10) {
          setJobStatus("error");
          setError("Lost connection to the server while extracting. Refresh the page and use 'Resume your last extraction' to continue.");
          toast.error("Connection lost — your extraction is still running on the server. Refresh to resume.");
          return;
        }
      }
    }
    setJobStatus("error");
    setError("Extraction timed out after 15 minutes. Try splitting the PDF into smaller batches (e.g. one month at a time).");
  };

  const pickFile = () => fileRef.current?.click();

  const uploadAndPoll = async (file) => {
    setBusy(true); setError(""); setEvents([]); setResult(null);
    // Discard any older un-consumed extraction so the resume banner
    // never silently re-appears for a future tab. (Banner UI was
    // removed per user 2026-02-19; this cleanup keeps the backend tidy.)
    try { await api.delete("/attendance/daily-pdf/jobs/latest"); } catch (_e) { /* silent */ }
    setFilename(file.name); setJobStatus("running");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const start = await api.post("/attendance/daily-pdf/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180_000,
      });
      const id = start.data?.job_id;
      if (!id) throw new Error("Backend didn't return a job id");
      setJobId(id);
      // Backend may already have a cached `done` result for this exact
      // PDF (same sha256). Skip polling and jump straight to preview.
      if (start.data?.status === "done") {
        const poll = await api.get(`/attendance/daily-pdf/jobs/${id}`);
        const j = poll.data || {};
        setEvents(Array.isArray(j.events) ? j.events : []);
        setJobStatus("done");
        toast.success(`Cached extraction restored — ${j.row_count} rows.`);
        return;
      }
      await pollUntilDone(id);
    } catch (err) {
      setJobStatus("error"); setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const importMatched = async () => {
    // Only push rows that matched the roster — unmatched are surfaced
    // for the director to fix in their child profiles, not silently dropped.
    const matched = events.filter((e) => e.matched);
    if (matched.length === 0) {
      toast.warning("No rows matched any enrolled child. Check your roster spellings.");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/attendance/daily-pdf/import", { events: matched, job_id: jobId });
      setResult(r.data || {});
      const { inserted = 0, deduped = 0, skipped = 0 } = r.data || {};
      toast.success(`Imported ${inserted} new event${inserted === 1 ? "" : "s"} · ${deduped} already on file · ${skipped} skipped.`);
      if (onImported) onImported();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); }
  };

  const matchedCount = events.filter((e) => e.matched).length;
  const unmatchedCount = events.length - matchedCount;
  // Group the preview by date for a cleaner read.
  const grouped = events.reduce((acc, e) => {
    const key = e.date || "—";
    (acc[key] = acc[key] || []).push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort();

  // Called from the "Use this child instead" button inside <NoRosterCell>.
  // We mutate the event row to point at the chosen roster_id and mark it
  // as a manual link — the backend matcher honours `roster_id` overrides
  // (see _match_events_to_roster), so on Import the row will get inserted
  // even though the CSV name didn't match anything on the roster.
  const relinkRow = (ev, rosterId, display) => {
    setEvents((prev) => prev.map((e) =>
      e === ev
        ? { ...e, matched: true, roster_id: rosterId, match_note: `manually linked to ${display}`, near_matches: undefined }
        : e
    ));
    toast.success(`Linked to ${display}.`);
  };

  return (
    <div data-testid="records-daily-pdf-importer" className="space-y-4">
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,application/pdf,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        data-testid="records-daily-pdf-file"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAndPoll(f); }}
      />

      {!jobStatus && (
        <div className="rounded-xl border border-dashed border-emerald-300 bg-emerald-50/20 p-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-emerald-600" />
          <p className="mt-2 text-sm font-medium text-stone-800">
            Drop a Daily Detailed Attendance file — Excel runs instantly, PDF takes 2-3&nbsp;minutes
          </p>
          <p className="text-xs text-stone-600 mt-1">
            Best results: export your KinderConnect / ProCare report as <strong>.xlsx</strong> or <strong>.csv</strong> for a
            sub-second import (no AI needed). PDFs work too but are slower. Only the child&apos;s arrival (IN)
            and departure (OUT) times are imported.
          </p>
          <Button
            data-testid="records-daily-pdf-upload-btn"
            onClick={pickFile}
            disabled={busy}
            className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-9 text-xs"
          >
            <Upload className="w-3.5 h-3.5 mr-1" /> Upload file
          </Button>
        </div>
      )}

      {jobStatus === "running" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-6 text-center" data-testid="records-daily-pdf-loading">
          <Loader2 className="w-8 h-8 mx-auto text-emerald-700 animate-spin" />
          <p className="mt-3 text-sm font-medium text-stone-800">Reading <span className="font-mono">{filename}</span>…</p>
          <p className="text-xs text-stone-600 mt-1" data-testid="records-daily-pdf-progress">
            {progressLabel || "AI is processing the PDF in parallel chunks of 4 pages. Big multi-month reports usually finish in 2–5 minutes."}
          </p>
        </div>
      )}

      {jobStatus === "error" && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-6" data-testid="records-daily-pdf-error">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-900">Extraction failed</div>
              <div className="text-xs text-red-800 mt-1">{error}</div>
            </div>
            <Button variant="outline" size="sm" onClick={reset} className="rounded-lg h-8 text-xs">Try again</Button>
          </div>
        </div>
      )}

      {jobStatus === "done" && events.length > 0 && !result && (
        <div className="space-y-3" data-testid="records-daily-pdf-preview">
          <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-stone-600">
              <span className="font-semibold text-stone-900">{events.length}</span> rows extracted from <span className="font-mono">{filename}</span>
              {" · "}
              <span className="text-emerald-700 font-medium">{matchedCount} match enrolled children</span>
              {unmatchedCount > 0 && <span className="text-amber-700"> · {unmatchedCount} unmatched</span>}
            </div>
            <div className="flex gap-2">
              <Button data-testid="records-daily-pdf-cancel" variant="outline" size="sm" onClick={reset} className="rounded-lg h-8 text-xs">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
              <Button
                data-testid="records-daily-pdf-import-btn"
                disabled={importing || matchedCount === 0}
                onClick={importMatched}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-8 text-xs"
              >
                {importing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Import all {matchedCount} matched
              </Button>
            </div>
          </div>

          {unmatchedCount > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {unmatchedCount} row{unmatchedCount === 1 ? "" : "s"} couldn&apos;t be linked to a child on your roster. Check
                that the names on the PDF match how the child is enrolled (Children page). Unmatched rows will NOT be imported.
              </span>
            </div>
          )}

          <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 sticky top-0 border-b border-stone-200">
                  <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                    <th className="text-left px-3 py-2 font-semibold">Child</th>
                    <th className="text-left px-3 py-2 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 font-semibold">IN</th>
                    <th className="text-left px-3 py-2 font-semibold">OUT</th>
                    <th className="text-right px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map((d) => (
                    <Fragment key={d}>
                      <tr className="bg-stone-50/50">
                        <td colSpan={5} className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{usDate(d)}</td>
                      </tr>
                      {grouped[d].map((e, i) => (
                        <tr key={`${d}-${i}`} data-testid={`records-daily-pdf-row-${d}-${i}`} className="border-t border-stone-100">
                          <td className="px-3 py-1.5 font-medium text-stone-900">{e.child_first} {e.child_last}</td>
                          <td className="px-3 py-1.5 text-stone-700 font-mono text-[11px]">{usDate(e.date)}</td>
                          <td className="px-3 py-1.5 text-stone-700 font-mono">{e.time_in || <span className="text-stone-300">—</span>}</td>
                          <td className="px-3 py-1.5 text-stone-700 font-mono">{e.time_out || <span className="text-stone-300">—</span>}</td>
                          <td className="px-3 py-1.5 text-right">
                            {e.matched ? (
                              e.match_note ? (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px]"
                                  title={e.match_note}
                                >
                                  <Sparkles className="w-2.5 h-2.5" /> {e.match_note.startsWith("manually") ? "Linked" : "Fuzzy"}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-2.5 h-2.5" /> Match</span>
                              )
                            ) : (
                              <NoRosterCell row={e} onLink={(rid, display) => relinkRow(e, rid, display)} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-2" data-testid="records-daily-pdf-result">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold">
            <Check className="w-4 h-4" /> Import complete
          </div>
          <div className="grid grid-cols-3 gap-3 text-center mt-2">
            <div className="rounded-lg bg-white border border-emerald-200 p-3">
              <div className="text-2xl font-display text-emerald-700">{result.inserted}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Inserted</div>
            </div>
            <div className="rounded-lg bg-white border border-stone-200 p-3">
              <div className="text-2xl font-display text-stone-700">{result.deduped}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Already on file</div>
            </div>
            <div className="rounded-lg bg-white border border-amber-200 p-3">
              <div className="text-2xl font-display text-amber-700">{result.skipped}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Skipped (no match)</div>
            </div>
          </div>
          {result.skipped_rows && result.skipped_rows.length > 0 && (
            <div className="mt-2 text-xs text-stone-600">
              Names that didn&apos;t match the roster:
              <div className="mt-1 flex flex-wrap gap-1">
                {result.skipped_rows.slice(0, 30).map((n, i) => (
                  <span key={i} className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">{n}</span>
                ))}
                {result.skipped_rows.length > 30 && <span className="text-[10px] text-stone-500">…and {result.skipped_rows.length - 30} more</span>}
              </div>
            </div>
          )}
          <div className="pt-3">
            <Button data-testid="records-daily-pdf-reset" variant="outline" size="sm" onClick={reset} className="rounded-lg h-8 text-xs">
              Upload another PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Records() {
  // Bump this counter when a new Daily PDF import completes so the
  // print archive auto-refreshes without making the director click a
  // reload button.
  const [archiveRefresh, setArchiveRefresh] = useState(0);
  const [features, setFeatures] = useState(null);
  useEffect(() => {
    api.get("/center/me")
      .then((r) => setFeatures(r.data?.records_features || { daily_importer: true, dcy01208_digital: true }))
      .catch(() => setFeatures({ daily_importer: false, dcy01208_digital: false }));
  }, []);

  return (
    <div data-testid="records-page" className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Records</div>
        <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Family & attendance records</h1>
        <p className="mt-2 text-stone-500">
          Tap any section to expand it. Cases, daily-detail PDF imports and the
          attendance archive all live here so directors don&apos;t have to dig
          through the Compliance vault for day-to-day work.
        </p>
      </header>

      <Collapsible
        id="cases"
        title="Case Closure Tracker"
        subtitle="Track every family's subsidy case · mark open/closed · notify parents within 14 days of expiry"
        Icon={Users}
        defaultOpen={false}
      >
        <CaseClosureTracker />
      </Collapsible>

      {features?.daily_importer && (
      <Collapsible
        id="daily-pdf"
        title="Digital Daily Importer"
        subtitle="AI reads a typed Daily Detailed Attendance report and auto-fills IN / OUT events for each child"
        Icon={Sparkles}
        defaultOpen={false}
      >
        <DailyPdfImporter onImported={() => setArchiveRefresh((n) => n + 1)} />
      </Collapsible>
      )}

      {features?.dcy01208_digital && (
      <Collapsible
        id="daily-print-archive"
        title="DCY 01208 Digital"
        subtitle="Every import re-renders the official Ohio DCY 01208 weekly attendance form, one sheet per classroom, with the IN/OUT times you uploaded already filled in. Sorted by year / month / week — ready to re-print for DCY audits anytime."
        Icon={Printer}
        defaultOpen={false}
      >
        <DailyPrintArchive refreshKey={archiveRefresh} />
      </Collapsible>
      )}

      <Collapsible
        id="archive"
        title="Attendance history"
        subtitle="Every week's sign-in/out data + uploaded teacher sheets, organised by year / month / week"
        Icon={CalendarRange}
        defaultOpen={false}
      >
        <AttendanceArchive />
      </Collapsible>

      <Collapsible
        id="audit-binder"
        title="State Audit Binder"
        subtitle="One-click export: every staff record, every child's forms, drill logs, and licensing docs bundled into a single ZIP — the button you hit the day licensing calls."
        Icon={FolderArchive}
        defaultOpen={false}
      >
        <AuditBinderPanel />
      </Collapsible>

      <Collapsible
        id="passcode"
        title="Records passcode"
        subtitle="Manage your Records passcode (separate from Staff Docs, Licensing, Compliance, and Billing) — owner-only."
        Icon={KeyRound}
        defaultOpen={false}
      >
        <div className="p-2">
          <p className="text-sm text-stone-700 mb-3">
            Each vault section now has its own passcode. Manage all of them — change, reset, or lock — from one place:
          </p>
          <Link
            data-testid="records-go-to-vault-settings"
            to="/dashboard/settings#settings-vault-card"
            className="inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11 px-5 text-sm font-semibold"
          >
            <ShieldCheck className="w-4 h-4" /> Open Center Settings → Vault
          </Link>
        </div>
      </Collapsible>
    </div>
  );
}

// Owner-only inline form to rotate the vault passcode. Lives inside the
// Records page (the user opens Records most often, so this is the natural
// home for the rotation control). On success, swaps the cached session
// token in sessionStorage so the new passcode is active immediately and
// no re-unlock is needed.
function ChangeVaultPasscode() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.trim().length < 4) { toast.error("New passcode must be at least 4 characters."); return; }
    if (next !== confirm) { toast.error("New passcode and confirmation don't match."); return; }
    if (next === current) { toast.error("New passcode must be different from the current one."); return; }
    setBusy(true);
    try {
      const r = await api.post("/vault/passcode/change", {
        current_passcode: current,
        new_passcode: next,
      });
      // Refresh the unlocked session so the user stays in.
      if (r.data?.token) sessionStorage.setItem("cc_vault_token", r.data.token);
      setCurrent(""); setNext(""); setConfirm("");
      setDone(true);
      toast.success("Vault passcode updated. Use the new passcode next time the vault locks.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="records-change-passcode-form" className="max-w-md space-y-3">
      <p className="text-xs text-stone-500 leading-relaxed">
        Enter your <b>current passcode</b> to confirm ownership, then pick a new 4–32 character passcode. The new passcode replaces the old one immediately — your current session stays unlocked for 30 more minutes.
      </p>
      <PasscodeField
        label="Current passcode"
        testId="records-passcode-current"
        value={current}
        onChange={setCurrent}
        autoFocus
      />
      <PasscodeField
        label="New passcode"
        testId="records-passcode-new"
        value={next}
        onChange={setNext}
      />
      <PasscodeField
        label="Confirm new passcode"
        testId="records-passcode-confirm"
        value={confirm}
        onChange={setConfirm}
      />
      <button
        type="submit"
        data-testid="records-passcode-submit"
        disabled={busy || !current || !next || !confirm}
        className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl h-11 font-medium inline-flex items-center justify-center gap-2"
      >
        {busy
          ? (<><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>)
          : (<><ShieldCheck className="w-4 h-4" /> Update passcode</>)}
      </button>
      {done && (
        <p data-testid="records-passcode-done" className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
          <Check className="w-3 h-3" /> Passcode updated. New passcode is active.
        </p>
      )}
    </form>
  );
}

function PasscodeField({ label, value, onChange, testId, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] uppercase tracking-widest font-semibold text-stone-700">{label}</label>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full h-11 px-3 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-emerald-700 text-base tracking-widest"
      />
    </div>
  );
}


// State Audit Binder — POSTs to /audit/binder to kick off a background job,
// polls /audit/binder/{job_id} every 3s while progress_note updates, then
// pulls the finished ZIP via axios blob (never a plain <a href> — that would
// strip the JWT + vault token).
function AuditBinderPanel() {
  const [status, setStatus] = useState("idle"); // idle | running | ready | failed
  const [progress, setProgress] = useState("");
  const [jobId, setJobId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [lastZip, setLastZip] = useState(null); // { filename, sizeBytes, headers }
  const pollRef = useRef(null);

  useEffect(() => () => {
    // clear any active poller when the component unmounts.
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const downloadReady = async (id) => {
    try {
      const res = await api.get(`/audit/binder/${id}`, { responseType: "blob" });
      // If still building the server sends 202 JSON — but axios follows through.
      // If we got a blob with application/zip, download it.
      const ct = res.headers?.["content-type"] || "";
      if (!ct.includes("application/zip")) {
        // Not ready yet — treat as still running.
        return false;
      }
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `Audit_Ready_${new Date().toISOString().slice(0,10)}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      setLastZip({
        filename,
        sizeBytes: blob.size,
        headers: {
          staff_active: res.headers?.["x-staff-active"],
          staff_dismissed: res.headers?.["x-staff-dismissed"],
          children_active: res.headers?.["x-children-active"],
          children_inactive: res.headers?.["x-children-inactive"],
          forms_rendered: res.headers?.["x-forms-rendered"],
          licensing_docs: res.headers?.["x-licensing-docs"],
          drill_years: res.headers?.["x-drill-years"],
        },
      });
      setStatus("ready");
      setProgress("Audit binder downloaded.");
      toast.success(`Audit binder ready — ${filename}`);
      return true;
    } catch (err) {
      // Any hard error (401/403/500/etc) — surface it and stop.
      setStatus("failed");
      setProgress(formatApiError(err));
      toast.error(formatApiError(err));
      return true; // stop polling on hard error
    }
  };

  const start = async () => {
    stopPolling();
    setStatus("running");
    setProgress("Queued…");
    setLastZip(null);
    try {
      const res = await api.post("/audit/binder");
      const id = res.data?.job_id;
      if (!id) throw new Error("No job id returned");
      setJobId(id);
      setStartedAt(new Date());
      pollRef.current = setInterval(async () => {
        try {
          const p = await api.get(`/audit/binder/${id}`);
          // Axios returns 202 as success with data = {status, progress_note}.
          if (p.status === 202 && p.data?.status === "running") {
            setProgress(p.data.progress_note || "Working…");
            return;
          }
        } catch (err) {
          // 202 hits the catch if we didn't opt in — treat as still running.
          if (err?.response?.status === 202) {
            const d = err.response.data || {};
            setProgress(d.progress_note || "Working…");
            return;
          }
          if (err?.response?.status === 404) { stopPolling(); setStatus("failed"); setProgress("Job expired — please regenerate."); toast.error("Job expired"); return; }
          if (err?.response?.status === 500) { stopPolling(); setStatus("failed"); setProgress(err.response.data?.detail || "Failed"); toast.error(err.response.data?.detail || "Audit binder failed"); return; }
        }
        // Try a blob download — will succeed once the job hits ready.
        const done = await downloadReady(id);
        if (done) stopPolling();
      }, 3000);
    } catch (err) {
      setStatus("failed");
      setProgress(formatApiError(err));
      toast.error(formatApiError(err));
    }
  };

  const elapsed = startedAt ? Math.max(0, Math.round((Date.now() - startedAt.getTime()) / 1000)) : 0;

  return (
    <div data-testid="audit-binder-panel" className="p-2 space-y-4">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-emerald-700 text-white grid place-items-center">
            <FolderArchive className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl text-stone-900">One-click state audit binder</h3>
            <p className="text-sm text-stone-600 mt-1 leading-relaxed">
              Bundles <b>every staff member</b> (checklists + uploaded docs + offboarding packet if dismissed),
              <b> every child</b>&apos;s filed DCY forms (active <em>and</em> inactive within retention window),
              <b> drill logs</b> for the last 12 months, and <b>every licensing document</b> on file
              into a single dated ZIP. Generation runs in the background — you can leave this page and come back.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-1">
          <Button
            data-testid="audit-binder-start-btn"
            onClick={start}
            disabled={status === "running"}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11 px-5"
          >
            {status === "running"
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              : <><FolderArchive className="w-4 h-4 mr-2" /> {status === "ready" ? "Regenerate audit binder" : "Generate audit binder"}</>}
          </Button>
          {status === "running" && (
            <div data-testid="audit-binder-status" className="text-sm text-stone-700">
              <span className="font-semibold">{progress}</span>
              <span className="text-stone-500"> · {elapsed}s elapsed</span>
            </div>
          )}
          {status === "failed" && (
            <div data-testid="audit-binder-failed" className="text-sm text-red-700 font-semibold">
              {progress}
            </div>
          )}
        </div>
      </div>

      {lastZip && (
        <div data-testid="audit-binder-ready" className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <Check className="w-4 h-4" /> Last binder downloaded
          </div>
          <div className="mt-1 text-sm text-stone-800 font-mono break-all">{lastZip.filename}</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {[
              ["Staff active",       lastZip.headers.staff_active],
              ["Staff dismissed",    lastZip.headers.staff_dismissed],
              ["Children active",    lastZip.headers.children_active],
              ["Children inactive",  lastZip.headers.children_inactive],
              ["Forms rendered",     lastZip.headers.forms_rendered],
              ["Licensing docs",     lastZip.headers.licensing_docs],
              ["Drill-log years",    lastZip.headers.drill_years],
              ["ZIP size",           `${(lastZip.sizeBytes / (1024 * 1024)).toFixed(1)} MB`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-stone-100 bg-stone-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{k}</div>
                <div className="font-semibold text-stone-900">{v ?? "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className="text-xs text-stone-500 pl-1">
        <summary className="cursor-pointer text-stone-600 font-semibold">What&apos;s inside the ZIP?</summary>
        <ul className="mt-2 space-y-1 list-disc pl-5 leading-relaxed">
          <li><code>staff/active/{"{Name}"}/</code> — checklist PDF + every uploaded compliance document (CPR, background check, medical statement, etc.).</li>
          <li><code>staff/dismissed/{"{Name}"}/</code> — same as active <em>plus</em> the signed Ohio Offboarding Checklist PDF.</li>
          <li><code>children/active/{"{Last_First}"}/</code> — every filed state form (DCY 01234, DCY 01218, DCY 01305, incident reports, …) rendered from the data currently on file.</li>
          <li><code>children/inactive/{"{Last_First}"}/</code> — same, for children within the 3-year retention window.</li>
          <li><code>drill_logs/drill_log_{"{year}"}.pdf</code> — printable fire · tornado · lockdown drill log for each of the last 12 months.</li>
          <li><code>licensing/{"{category}"}/</code> — center-level licensing docs (fire inspection, health inspection, insurance, playground safety, evacuation plan, etc.).</li>
          <li><code>MANIFEST.txt</code> — one-page summary of counts + generation info the inspector can skim first.</li>
        </ul>
      </details>
    </div>
  );
}

